import { Injectable, NgZone, inject, signal } from '@angular/core'
import { OpenAPI } from '@trailup/contracts'
import type {
  AdminAuthResponseDto,
  AdminAuthCapabilitiesResponseDto,
  AdminPasskeyAuthenticationOptionsResponseDto,
  AdminPasskeyListResponseDto,
  AdminPasskeyRegistrationOptionsResponseDto,
  AdminPasskeyResponseDto,
  AdminPrincipalDto,
} from '@trailup/contracts'
import type {
  AuthenticationResponseJSON,
  RegistrationResponseJSON,
} from '@simplewebauthn/browser'

export class AdminAuthHttpError extends Error {
  constructor(
    readonly status: number,
    readonly code: string | null
  ) {
    super(`Auth request failed (${status})`)
  }
}

const ACCESS_TOKEN_KEY = 'admin_access_token'
const PRINCIPAL_KEY = 'admin_principal'

@Injectable({ providedIn: 'root' })
export class AdminAuthService {
  private readonly ngZone = inject(NgZone)
  private readonly accessTokenSignal = signal<string | null>(
    this.readStoredToken()
  )
  private readonly principalSignal = signal<AdminPrincipalDto | null>(
    this.readStoredPrincipal()
  )

  private refreshPromise: Promise<boolean> | null = null

  readonly accessToken = this.accessTokenSignal.asReadonly()
  readonly principal = this.principalSignal.asReadonly()

  constructor() {
    OpenAPI.BASE = ''
    OpenAPI.WITH_CREDENTIALS = true
    OpenAPI.CREDENTIALS = 'include'
    OpenAPI.TOKEN = async () => this.accessTokenSignal() ?? ''
  }

  isAuthenticated(): boolean {
    return this.accessTokenSignal() !== null
  }

  async getCapabilities(): Promise<AdminAuthCapabilitiesResponseDto> {
    const response = await fetch('/api/admin/auth/capabilities', {
      cache: 'no-store',
      credentials: 'include',
    })
    if (!response.ok) throw new AdminAuthHttpError(response.status, null)
    return (await response.json()) as AdminAuthCapabilitiesResponseDto
  }

  async loginWithPassword(email: string, password: string): Promise<void> {
    const response = await this.postJson<AdminAuthResponseDto>(
      '/api/admin/auth/login',
      { email, password }
    )
    this.setAuthState(response)
  }

  async loginWithGoogle(idToken: string): Promise<void> {
    await this.runInAngularZone(async () => {
      const response = await this.postJson<AdminAuthResponseDto>(
        '/api/admin/auth/social/google',
        { idToken }
      )
      this.setAuthState(response)
    })
  }

  async loginWithPasskey(credential: {
    ceremonyId: string
    credential: AuthenticationResponseJSON
  }): Promise<void> {
    await this.runInAngularZone(async () => {
      const response = await this.postJson<AdminAuthResponseDto>(
        '/api/admin/auth/passkeys/authentication/verify',
        credential
      )
      this.setAuthState(response)
    })
  }

  getPasskeyAuthenticationOptions(): Promise<AdminPasskeyAuthenticationOptionsResponseDto> {
    return this.postJson('/api/admin/auth/passkeys/authentication/options', {})
  }

  getPasskeyRegistrationOptions(): Promise<AdminPasskeyRegistrationOptionsResponseDto> {
    return this.authorizedRequest(
      '/api/admin/auth/passkeys/registration/options',
      'POST'
    )
  }

  async verifyPasskeyRegistration(
    ceremonyId: string,
    credential: RegistrationResponseJSON,
    name?: string
  ): Promise<AdminPasskeyResponseDto> {
    return this.authorizedRequest(
      '/api/admin/auth/passkeys/registration/verify',
      'POST',
      { ceremonyId, credential, name }
    )
  }

  listPasskeys(): Promise<AdminPasskeyListResponseDto> {
    return this.authorizedRequest('/api/admin/auth/passkeys', 'GET')
  }

  renamePasskey(id: string, name: string): Promise<AdminPasskeyResponseDto> {
    return this.authorizedRequest(`/api/admin/auth/passkeys/${id}`, 'PATCH', {
      name,
    })
  }

  revokePasskey(id: string): Promise<{ revoked: boolean }> {
    return this.authorizedRequest(`/api/admin/auth/passkeys/${id}`, 'DELETE')
  }

  async getAuthorizedBlob(url: string, retry = true): Promise<Blob> {
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${this.accessTokenSignal() ?? ''}`,
      },
    })

    if (response.status === 401 && retry && (await this.refresh())) {
      return this.getAuthorizedBlob(url, false)
    }
    if (!response.ok) {
      throw new AdminAuthHttpError(
        response.status,
        await this.readErrorCode(response)
      )
    }

    return response.blob()
  }

  refresh(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null
      })
    }
    return this.refreshPromise
  }

  private async performRefresh(): Promise<boolean> {
    return this.runInAngularZone(async () => {
      try {
        const response = await this.postJson<AdminAuthResponseDto>(
          '/api/admin/auth/refresh',
          {}
        )
        this.setAuthState(response)
        return true
      } catch {
        this.clearAuthState()
        return false
      }
    })
  }

  async logout(): Promise<void> {
    await this.runInAngularZone(async () => {
      try {
        await this.postJson('/api/admin/auth/logout', {})
      } finally {
        this.clearAuthState()
      }
    })
  }

  private setAuthState(response: AdminAuthResponseDto): void {
    this.accessTokenSignal.set(response.accessToken)
    this.principalSignal.set(response.principal)
    sessionStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken)
    sessionStorage.setItem(PRINCIPAL_KEY, JSON.stringify(response.principal))
  }

  clearAuthState(): void {
    this.accessTokenSignal.set(null)
    this.principalSignal.set(null)
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(PRINCIPAL_KEY)
  }

  private async postJson<T>(url: string, body: unknown): Promise<T> {
    const response = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      throw new Error(`Auth request failed (${response.status})`)
    }

    return (await response.json()) as T
  }

  private async authorizedRequest<T>(
    url: string,
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    body?: unknown,
    retry = true
  ): Promise<T> {
    const response = await fetch(url, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.accessTokenSignal() ?? ''}`,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    })

    if (response.status === 401 && retry && (await this.refresh())) {
      return this.authorizedRequest(url, method, body, false)
    }
    if (!response.ok) {
      throw new AdminAuthHttpError(
        response.status,
        await this.readErrorCode(response)
      )
    }

    return (await response.json()) as T
  }

  private async readErrorCode(response: Response): Promise<string | null> {
    try {
      const error: unknown = await response.json()
      return typeof error === 'object' &&
        error !== null &&
        'code' in error &&
        typeof error.code === 'string'
        ? error.code
        : null
    } catch {
      return null
    }
  }

  private readStoredToken(): string | null {
    return sessionStorage.getItem(ACCESS_TOKEN_KEY)
  }

  private readStoredPrincipal(): AdminPrincipalDto | null {
    const raw = sessionStorage.getItem(PRINCIPAL_KEY)
    if (!raw) {
      return null
    }

    try {
      return JSON.parse(raw) as AdminPrincipalDto
    } catch {
      return null
    }
  }

  private async runInAngularZone<T>(operation: () => Promise<T>): Promise<T> {
    try {
      const result = await operation()
      return await new Promise<T>((resolve) => {
        this.ngZone.run(() => resolve(result))
      })
    } catch (error) {
      return await new Promise<T>((_resolve, reject) => {
        this.ngZone.run(() => reject(error))
      })
    }
  }
}
